import type {
  EditorState,
  TrackType,
} from "../../vendor/src/features/remotion-editor/editor/state/types";
import type { EditorStarterItem } from "../../vendor/src/features/remotion-editor/editor/items/item-type";
import type { EditorStarterAsset } from "../../vendor/src/features/remotion-editor/editor/assets/assets";
import { dropTimelineSelection, type DropTarget } from "./timeline-drop";
import { hasPendingTimelineEdit } from "./edit-ownership";
import { itemTrackKind, orderTrackZones, type TrackKind } from "./track-layout";

export type InsertionTarget =
  | { type: "existing"; trackId: string }
  | {
      type: "new";
      beforeTrackId: string | null;
      edge: "front" | "back";
      bgm?: boolean;
    };
export function captureInsertionTarget(
  tracks: readonly TrackType[],
  index?: number | null,
  forceNew = false,
): InsertionTarget {
  if (index !== undefined && index !== null && !Number.isSafeInteger(index))
    throw new Error("추가할 트랙 위치를 다시 선택해 주세요.");
  if (index === undefined || index === null || index < 0)
    return { type: "new", beforeTrackId: null, edge: "front" };
  if (index >= tracks.length)
    return { type: "new", beforeTrackId: null, edge: "back" };
  return forceNew
    ? { type: "new", beforeTrackId: tracks[index]!.id, edge: "back" }
    : { type: "existing", trackId: tracks[index]!.id };
}
export type InsertMediaResult =
  | { ok: true; state: EditorState; itemId: string; assetId: string }
  | {
      ok: false;
      state: EditorState;
      reason:
        | "pending-edit"
        | "invalid-media"
        | "id-conflict"
        | "target-removed"
        | "placement-rejected"
        | "fps-changed";
    };

/** Index is presentation only; persistent intent is an existing row id or a stable insertion neighbor. */
export function incomingTrackIndex(
  tracks: readonly TrackType[],
  target: InsertionTarget,
  kind: TrackKind,
): number | null {
  if (target.type === "existing") {
    const index = tracks.findIndex((t) => t.id === target.trackId);
    return index < 0 ? null : index;
  }
  const anchor =
    target.beforeTrackId === null
      ? -1
      : tracks.findIndex((t) => t.id === target.beforeTrackId);
  if (target.beforeTrackId !== null && anchor < 0) return null;
  const index =
    anchor >= 0
      ? anchor
      : kind === "audio" || target.edge === "back"
        ? tracks.length
        : 0;
  const boundary = tracks.findIndex((t) => t.zone === "audio");
  const visualEnd = boundary < 0 ? tracks.length : boundary;
  return kind === "audio"
    ? Math.max(visualEnd, index)
    : Math.min(visualEnd, index);
}
const safeId = (id: string) =>
  typeof id === "string" &&
  id.length > 0 &&
  id.length < 512 &&
  !["__proto__", "prototype", "constructor"].includes(id);

/** Add exactly one prepared source asset. An invisible staging row is never committed to the document. */
export function insertPreparedMedia(
  state: EditorState,
  item: EditorStarterItem,
  asset: EditorStarterAsset,
  target: InsertionTarget,
  expectedFps: number,
): InsertMediaResult {
  const fail = (
    reason: Extract<InsertMediaResult, { ok: false }>["reason"],
  ): InsertMediaResult => ({ ok: false, state, reason });
  if (hasPendingTimelineEdit(state)) return fail("pending-edit");
  if (state.undoableState.fps !== expectedFps) return fail("fps-changed");
  if (
    !Number.isFinite(expectedFps) ||
    expectedFps <= 0 ||
    !safeId(item.id) ||
    !safeId(asset.id) ||
    !Number.isSafeInteger(item.from) ||
    item.from < 0 ||
    !Number.isSafeInteger(item.durationInFrames) ||
    item.durationInFrames < 1 ||
    !("assetId" in item) ||
    item.assetId !== asset.id
  )
    return fail("invalid-media");
  const expectedType =
    item.type === "captions"
      ? "caption"
      : item.type === "ppt"
        ? "pdf"
        : item.type;
  if (asset.type !== expectedType) return fail("invalid-media");
  if (Object.hasOwn(state.undoableState.items, item.id))
    return fail("id-conflict");
  const existing = state.undoableState.assets[asset.id];
  if (existing && JSON.stringify(existing) !== JSON.stringify(asset))
    return fail("id-conflict");
  const kind = itemTrackKind(item);
  let insertion: DropTarget;
  if (target.type === "existing") {
    if (!state.undoableState.tracks.some((t) => t.id === target.trackId))
      return fail("target-removed");
    insertion = { trackId: target.trackId };
  } else {
    const index = incomingTrackIndex(state.undoableState.tracks, target, kind);
    if (index === null) return fail("target-removed");
    let trackId = "import-track-" + item.id;
    let suffix = 0;
    while (state.undoableState.tracks.some((t) => t.id === trackId))
      trackId = "import-track-" + item.id + "-" + ++suffix;
    insertion = { newTrack: { id: trackId, index, kind } };
  }
  let stageId = "import-stage-" + item.id;
  let suffix = 0;
  while (state.undoableState.tracks.some((t) => t.id === stageId))
    stageId = "import-stage-" + item.id + "-" + ++suffix;
  const stage: TrackType = {
    id: stageId,
    items: [item.id],
    zone: kind === "audio" ? "audio" : "visual",
    locked: false,
    hidden: false,
    muted: false,
    magnetic: false,
    metadata: { type: kind, isMainTrack: false, trackType: null },
  };
  const prepared: EditorState = {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: { ...state.undoableState.items, [item.id]: item },
      assets: existing
        ? state.undoableState.assets
        : { ...state.undoableState.assets, [asset.id]: asset },
      tracks: orderTrackZones([...state.undoableState.tracks, stage]),
    },
  };
  // Resolve the new-row anchor against the pre-staging document; its relative visual/audio order is stable.
  const result = dropTimelineSelection(
    prepared,
    [item.id],
    item.id,
    item.from,
    insertion,
  );
  if (!result.ok) return fail("placement-rejected");
  const tracks = result.state.undoableState.tracks
    .filter((t) => t.id !== stageId)
    .map((t) =>
      target.type === "new" && target.bgm && t.items.includes(item.id)
        ? { ...t, metadata: { ...t.metadata!, kind: "bgm" as const } }
        : t,
    );
  return {
    ok: true,
    itemId: item.id,
    assetId: asset.id,
    state: {
      ...result.state,
      assetStatus: existing
        ? state.assetStatus
        : {
            ...state.assetStatus,
            [asset.id]: {
              type:
                (asset.remoteUrl && /^https?:\/\//i.test(asset.remoteUrl)) ||
                asset.type === "caption"
                  ? "uploaded"
                  : "pending-upload",
            },
          },
      undoableState: { ...result.state.undoableState, tracks },
    },
  };
}
